import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { TrackFunction } from 'support/helpers/analytics/analytics';
import { PropertyFilter, PropertyFilterType } from 'support/types/filters';
import { MenuButton, MenuButtonNestedOption } from 'components/MenuButton';

import { ClearAllFiltersRow } from './components/ClearAllFiltersRow';
import { FilterDatePanel } from './components/FilterDatePanel/FilterDatePanel';
import { FilterDropdownPanel } from './components/FilterDropdownPanel/FilterDropdownPanel';
import { FilterDropdownPanelWithSubOptions } from './components/FilterDropdownPanelWithSubOptions/FilterDropdownPanelWithSubOptions';
import { SaveAsDefaultRow } from './components/SaveAsDefaultRow';
import {
  getActiveCount,
  getFilterLabel,
  HIDDEN_TYPES,
  isFilterActive,
  resolveRowIcon,
  resolveTriggerIcon,
} from './helpers';

type FiltersMenuProps = {
  propertyFilters: Array<PropertyFilter>;
  onChange: (filterKey: string, newValue: string) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
  track: TrackFunction;
  // When provided, a "Save as Default" row is shown that persists the current filters.
  onSaveAsDefault?: () => void;
};

export const FiltersMenu: FC<FiltersMenuProps> = ({
  propertyFilters,
  onChange,
  clearAllFilters,
  hasActiveFilters,
  track,
  onSaveAsDefault,
}) => {
  const { t } = useTranslation();

  const menuFilters = propertyFilters.filter((filter) => !HIDDEN_TYPES.includes(filter.type));

  if (!menuFilters.length) {
    return null;
  }

  const activeFilterCount = menuFilters.filter(isFilterActive).length;

  const handleChange = (filter: PropertyFilter) => (filterKey: string, newValue: string) => {
    if (filter.eventDetails) {
      track(filter.eventDetails.event, filter.eventDetails.properties);
    }
    onChange(filterKey, newValue);
  };

  return (
    <MenuButton
      text={t('common:components.filters.title')}
      Icon={resolveTriggerIcon(activeFilterCount)}
      variant="secondary"
      className="min-h-[38px]"
      testId="filters-menu"
      analyticsId="filters:menu:trigger"
    >
      {menuFilters.map((filter) => {
        const count = getActiveCount(filter);

        return (
          <MenuButtonNestedOption
            key={filter.key}
            label={getFilterLabel(filter)}
            Icon={resolveRowIcon(filter, count)}
            testId={`filters-menu-row-${filter.key}`}
            analyticsId={`filters:menu:row:${filter.key}`}
          >
            {filter.type === PropertyFilterType.date ? (
              <FilterDatePanel filter={filter} onChange={handleChange(filter)} />
            ) : filter.dropDownOptions?.subOptions ? (
              <FilterDropdownPanelWithSubOptions filter={filter} onChange={handleChange(filter)} />
            ) : (
              <FilterDropdownPanel filter={filter} onChange={handleChange(filter)} />
            )}
          </MenuButtonNestedOption>
        );
      })}

      {hasActiveFilters && <ClearAllFiltersRow onClear={clearAllFilters} />}
      {onSaveAsDefault && <SaveAsDefaultRow onSave={onSaveAsDefault} newGroup={!hasActiveFilters} />}
    </MenuButton>
  );
};
